import React, { useState, useEffect, useRef, MutableRefObject } from "react";
import { useStaticQuery, graphql, withPrefix } from "gatsby";

// Hooks //
import useBrowserMode from "../../../shared/hooks/useBrowserMode";
import useMetaSearch from "../../../shared/hooks/useMetaSearch";

import { normalizeTwoWords } from "../../../shared/utils/stringUtilities";

// Services //
import { getVacancies } from "../../../services/vacante.services";
import { getAccessToken } from "../../../services/accessToken";

// Styles //
import "./searcher.scss";

const Searcher = (props: SearcherInterface) => {
    const data = useStaticQuery(graphql`
        query SEARCHER_PLACEHOLDER {
            allContentfulHome(filter: { titulo: { eq: "Home Empleo 2.0" } }) {
                nodes {
                    placeholdersBuscador
                }
            }
        }
    `);

    const placeholders = data.allContentfulHome.nodes[0].placeholdersBuscador;
    const {
        compact,
        type,
        onSelect,
        scrollIntoQuestion,
        defaultPlaceholder,
        onClose, 
    } = props;

    const node: MutableRefObject<any> = useRef();
    const [isActive, setActiveSelect] = useState(false);
    const [dynamicPlaceholder, setDynamicPlaceholder] = useState("");
    const [searchWord, setSearchWord] = useState("");
    const { filterSearch, doSearch } = useMetaSearch(type);
    const { isBrowser, window, document } = useBrowserMode();
    const [searchResult, setSearchResult] = useState([]);
     const [isVisible, setIsVisible] = useState(true);

    const handleClickOutside = (e: Event) => {
        if (node.current.contains(e.target)) {
            return;
        } else {
            setActiveSelect(false);
        }
    };

    const handleClose = () => {
        // Reiniciar estados
        setActiveSelect(false);
        setSearchWord("");
        doSearch("");
        searchVacancies("");
        
        // Quitar el foco del input
        if (isBrowser && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        
        // Ocultar overlay si existe
        const overlay = document.querySelector('.m-searcher-overlay');
        if (overlay instanceof HTMLElement) {
            overlay.style.display = 'none';
        }

        if (onClose && typeof onClose === 'function') {
            onClose();
          }
    };

    const getData = async (value: string | number) => {
        let token = await (await getAccessToken()).data.data.accessToken;

        await getVacancies(value, token)
            .then((response) => {
                const vacantes = response.data.data.vacantes.content.map(
                    (vacancie) => ({
                        slug:
                            "/personas/vacantes-en-antioquia/vacante/?id=" +
                            vacancie.referenceNumber,
                        titulo: vacancie.title,
                        categoria: "Vacantes",
                        tipoContenido: "Subcategoría",
                    })
                );
                setSearchResult(vacantes);
            })
            .catch((err) => console.log(err));
    };

    const searchVacancies = async (value: string) => {
        if (value) {
            await getData(value);
        }
    };

    useEffect(() => {
        if (type === "empleo") {
            let cont = 0;

            const timer = setInterval(() => {
                if (cont > 2) cont = 0;
                setDynamicPlaceholder(`${placeholders[cont]}`);
                cont++;
            }, 1200);

            return () => clearInterval(timer);
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isActive]);

    const renderFormattedText = (text, word) => {
        const comparingText =
            text !== null && text.toLowerCase().indexOf(word.toLowerCase());

        const formattedText =
            text !== null && comparingText !== -1 ? (
                <React.Fragment>
                    {text.slice(0, comparingText)}
                    <span>
                        {text.slice(comparingText, comparingText + word.length)}
                    </span>
                    {text.slice(comparingText + word.length)}
                </React.Fragment>
            ) : (
                text
            );

        return formattedText;
    };
  
    return (
        <div
            className={`m-searcher-container ${
                compact ? "m-searcher-container--compact" : ""
            } ${
                type === "empleo"
                    ? "m-searcher-container--compact-extended"
                    : ""
            }`}
        >
            <div
                className={`m-searcher-container m-searcher-container__content ${
                    isActive || searchWord.trim().length > 2
                        ? "m-searcher-container__content--active"
                        : ""
                }`}
            >
                <div className="m-searcher-container__input">
                    <i
                        className="icon icon-search"
                        onClick={() =>
                            isBrowser &&
                            searchWord.trim().length > 2 &&
                            window.location.assign(
                                withPrefix(
                                    `/resultados-busqueda/?search=${searchWord}&sort=ASC`
                                )
                            )
                        }
                    />
                    <input
                        autoFocus
                        ref={node}
                        value={searchWord}
                        placeholder={
                            type === "preguntas"
                                ? "¿Qué estás buscando?"
                                : defaultPlaceholder || dynamicPlaceholder
                        }
                        onClickCapture={() => setActiveSelect(true)}
                        onChange={(text) => {
                            doSearch(text.currentTarget.value);
                            searchVacancies(text.currentTarget.value);
                            setSearchWord(text.currentTarget.value);
                        }}
                    />
                    {searchWord.trim().length > 2 && (
                        <i
                            className="icon icon-times-circle1"
                            onClickCapture={() => {
                                setSearchWord("");
                                doSearch("");
                                searchVacancies("");
                            }}
                        />
                    )}
                </div>
                {(searchWord.trim().length > 2 || !compact) && (
                    <div className="m-searcher-container__separator" />
                )}
                {(searchWord.trim().length > 2 || !compact) && (
                    <div className="m-searcher-container__results">
                        <ul>
                            {type === "empleo" &&
                                filterSearch &&
                                [...filterSearch, ...searchResult].length === 0 && (
                                    <li>
                                        <span className="m-searcher-results__title">
                                            {`En este momento no encontramos resultados para "${searchWord.trim()}"`}
                                        </span>
                                    </li>
                                )}
                            {filterSearch &&
                                [...filterSearch, ...searchResult].map(
                                    (result, index) => (
                                        <li
                                            key={`result${index}`}
                                            onClick={() => {
                                                if (
                                                    onSelect !== undefined &&
                                                    scrollIntoQuestion !==
                                                        undefined
                                                ) {
                                                    onSelect(result);
                                                    doSearch("");
                                                    setSearchWord("");
                                                    searchVacancies("");
                                                    scrollIntoQuestion(
                                                        result.titulo
                                                    );
                                                }
                                            }}
                                        >
                                            <a
                                                href={
                                                    type === "empleo"
                                                        ? `${withPrefix(
                                                              result.slug
                                                          )}`
                                                        : "#"
                                                }
                                                target="_self"
                                                rel="noreferrer"
                                                className={`gtmResultadosPredictivo${normalizeTwoWords(
                                                    type === "empleo"
                                                        ? result.titulo
                                                        : result.tituloBanner
                                                )}${normalizeTwoWords(
                                                    result.tipoContenido
                                                )}${normalizeTwoWords(
                                                    result.categoria
                                                )}`}
                                            >
                                                <p
                                                    className={`gtmResultadosPredictivo${normalizeTwoWords(
                                                        type === "empleo"
                                                            ? result.titulo
                                                            : result.tituloBanner
                                                    )}${normalizeTwoWords(
                                                        result.tipoContenido
                                                    )}${normalizeTwoWords(
                                                        result.categoria
                                                    )}`}
                                                >
                                                    <span
                                                        className={`m-searcher-results__title 
                                       gtmResultadosPredictivo${normalizeTwoWords(
                                           type === "empleo"
                                               ? result.titulo
                                               : result.tituloBanner
                                       )}${normalizeTwoWords(
                                                            result.tipoContenido
                                                        )}${normalizeTwoWords(
                                                            result.categoria
                                                        )}`}
                                                    >
                                                        {renderFormattedText(
                                                            type === "empleo"
                                                                ? result.titulo
                                                                : result.tituloBanner,
                                                            searchWord
                                                        )}
                                                    </span>
                                                    <br />
                                                    <span
                                                        className={`m-searcher-results__subtitle
                                       gtmResultadosPredictivo${normalizeTwoWords(
                                           type === "empleo"
                                               ? result.titulo
                                               : result.tituloBanner
                                       )}${normalizeTwoWords(
                                                            result.tipoContenido
                                                        )}${normalizeTwoWords(
                                                            result.categoria
                                                        )}
                                 `}
                                                    >
                                                        {type === "empleo" &&
                                                            renderFormattedText(
                                                                result.tipoContenido,
                                                                searchWord
                                                            )}
                                                        {type === "empleo" &&
                                                            ": "}
                                                        {renderFormattedText(
                                                            result.categoria,
                                                            searchWord
                                                        )}
                                                    </span>
                                                </p>
                                            </a>
                                        </li>
                                    )
                                )}
                        </ul>
                    </div>
                )}
                   <div 
                    className="m-searcher-container__close-button"
                    onClick={handleClose}
                    style={{
                        position: 'absolute',
                        top: '40%',
                        right: '10px',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        borderRadius: '50%',
                        zIndex: 10
                    }}
                >
                    <div style={{
                        position: 'relative',
                        width: '20px',
                        height: '20px'
                    }}>
                        <span style={{
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            width: '100%',
                            height: '2px',
                            backgroundColor: '#ee2b7b',
                            transform: 'rotate(45deg)'
                        }}></span>
                        <span style={{
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            width: '100%',
                            height: '2px',
                            backgroundColor: '#ee2b7b',
                            transform: 'rotate(-45deg)'
                        }}></span>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface SearcherInterface {
    compact?: boolean;
    type?: "comfama" | "preguntas" | "redamor" | "empleo";
    onSelect?: Function;
    scrollIntoQuestion?: Function;
    defaultPlaceholder?: string;
    onClose?: Function;
}

export default Searcher;
